import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLANS = [
  {
    key: "noble", name: "NOBLE", price: 299, img: "/assets/crown-noble.png",
    border: "border-amber-700/60", glow: "rgba(205,127,50,0.3)",
    perks: ["VIP entrance effect", "Bronze crown theme", "Monthly gift box", "24/7 support"],
  },
  {
    key: "royal", name: "ROYAL", price: 999, img: "/assets/crown-royal.png",
    border: "border-purple-400/60", glow: "rgba(160,110,255,0.35)",
    perks: ["Premium entrance", "Silver crown & theme", "Curated luxury box", "Priority access", "Special events"],
  },
  {
    key: "maharaja", name: "MAHARAJA", price: 4999, img: "/assets/crown-maharaja.png",
    border: "border-[#FF007F]", glow: "rgba(255,0,127,0.45)", best: true,
    perks: ["Ultimate entrance spectacle", "Gold Maharaja crown", "Bespoke gift box", "Private concierge", "Lifetime elite status", "Access to all levels"],
  },
];

export default function VIP() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [selected, setSelected] = useState("maharaja");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, []);

  const vipActive = user?.vip_until && user.vip_until > new Date().toISOString();
  const plan = PLANS.find((p) => p.key === selected);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/vip/subscribe", { plan: selected });
      setUser(data.user);
      toast.success(data.message);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen max-w-md mx-auto py-6 relative overflow-hidden" data-testid="vip-page">
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-48 rounded-full bg-gradient-to-r from-[#FF007F]/25 via-[#FF9E4D]/25 to-[#E6B84C]/25 blur-3xl pointer-events-none" />

      <div className="relative flex items-center justify-between px-4 mb-1">
        <button data-testid="vip-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-widest">VIP CLUB</h1>
        <span className="w-5" />
      </div>
      <p className="relative text-zinc-500 text-[11px] text-center mb-6">Exclusive crowns, entrances & elite privileges</p>

      {vipActive && (
        <div className="relative mx-4 mb-5 glass rounded-2xl px-4 py-3 border border-[#E6B84C]/40 flex items-center gap-2" data-testid="vip-active-banner">
          <img src="/assets/crown-maharaja.png" alt="" className="w-8 h-8 object-contain" />
          <p className="text-[#E6B84C] text-xs font-bold">
            {String(user.vip_plan || "VIP").toUpperCase()} active until {String(user.vip_until).slice(0, 10)}
          </p>
        </div>
      )}

      <div ref={scrollRef} className="relative flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory px-4 pb-2 pt-4">
        {PLANS.map((t, i) => (
          <motion.button key={t.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
            onClick={() => setSelected(t.key)} data-testid={`vip-plan-${t.key}`}
            className={`relative shrink-0 snap-center w-[230px] glass rounded-3xl p-5 pt-7 text-left border-2 transition-all ${selected === t.key ? t.border : "border-white/10"}`}
            style={{ boxShadow: selected === t.key ? `0 0 28px ${t.glow}` : "none" }}>
            {t.best && (
              <span className="absolute -top-3 right-4 bg-gradient-to-r from-[#E6B84C] to-[#FDE08B] text-[#0B0F19] text-[10px] font-extrabold px-3.5 py-1 rounded-full shadow-[0_0_12px_rgba(230,184,76,0.7)]" data-testid="vip-best-badge">BEST</span>
            )}
            <div className="flex flex-col items-center mb-3">
              <span className="absolute top-2 w-24 h-16 rounded-full blur-2xl" style={{ background: t.glow }} />
              <img src={t.img} alt={t.name} className="relative w-20 h-20 object-contain drop-shadow-[0_6px_16px_rgba(0,0,0,0.5)]" />
              <h2 className="font-royal text-lg font-bold gradient-gold-text tracking-[0.2em] mt-1">{t.name}</h2>
            </div>
            <ul className="space-y-1.5 min-h-[120px]">
              {t.perks.map((p) => (
                <li key={p} className="text-[10.5px] text-zinc-300 flex items-start gap-1.5"><span className="text-[#E6B84C] mt-px">✦</span> {p}</li>
              ))}
            </ul>
            <p className="text-center mt-3">
              <span className="font-royal text-xl font-bold gradient-gold-text">₹{t.price.toLocaleString("en-IN")}</span>
              <span className="text-zinc-500 text-[10px] font-semibold">/mo</span>
            </p>
          </motion.button>
        ))}
      </div>

      <div className="relative px-4 mt-6">
        <button data-testid="vip-upgrade-button" onClick={subscribe} disabled={busy}
          className="w-full bg-gradient-to-r from-[#E6B84C] via-[#FDE08B] to-[#E6B84C] text-[#0B0F19] font-extrabold font-display py-4 rounded-full shadow-[0_0_24px_rgba(230,184,76,0.55)] active:scale-[0.98] transition-transform disabled:opacity-50 tracking-wide">
          {busy ? "Processing…" : `UPGRADE TO ${plan.name}`}
        </button>
        <p className="font-royal text-center text-sm mt-4 bg-gradient-to-r from-[#E6B84C] via-[#FDE08B] to-[#FF007F] bg-clip-text text-transparent font-bold">
          ⸙ Rise from Noble to Maharaja ⸙
        </p>
        <p className="text-zinc-600 text-[10px] text-center mt-2">Mock payment in dev — 30 days added instantly · VIP gives +10% coins on every recharge</p>
      </div>
    </div>
  );
}
